import { type ImgHTMLAttributes } from 'react';

interface LogoProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  variant?: 'full' | 'emblem' | 'wordmark';
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Daily Reprieve Logo Component
 *
 * Uses the official mountain emblem logo from Brand Specification v1.0
 *
 * Variants:
 * - full: Complete emblem with mountains, sun, and "DAILY REPRIEVE" text (default)
 * - emblem: Logo mark only (no text) - for app icons, favicons, avatars
 * - wordmark: Text-only "Daily Reprieve" in Inter Bold
 *
 * Brand Symbolism:
 * - Mountains: The uphill climb of recovery, strength, solid foundation
 * - Rising Sun: New beginning every 24 hours, light breaking through darkness
 * - Circle: Unity of fellowship, connection to 12-Step heritage
 */

// Logo Emblem (WebP with PNG fallback for broad browser support)
export function LogoEmblem({
  size = 'md',
  className = '',
  ...props
}: Omit<LogoProps, 'variant'>) {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24',
  };

  return (
    <picture>
      <source srcSet="/assets/logo/daily-reprieve-full.webp" type="image/webp" />
      <img
        src="/assets/logo/daily-reprieve-full.png"
        alt="Daily Reprieve"
        className={`${sizeClasses[size]} ${className}`}
        {...props}
      />
    </picture>
  );
}

// Simplified emblem for very small sizes
export function LogoIcon({
  size = 'sm',
  className = '',
  ...props
}: Omit<LogoProps, 'variant'>) {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
    xl: 'w-12 h-12',
  };

  return (
    <picture>
      <source srcSet="/assets/logo/daily-reprieve-full.webp" type="image/webp" />
      <img
        src="/assets/logo/daily-reprieve-full.png"
        alt="Daily Reprieve"
        className={`${sizeClasses[size]} rounded-full ${className}`}
        {...props}
      />
    </picture>
  );
}

// Wordmark only (text)
export function LogoWordmark({
  size = 'md',
  className = '',
}: {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}) {
  const sizeClasses = {
    sm: 'text-base',
    md: 'text-xl',
    lg: 'text-2xl',
    xl: 'text-3xl',
  };

  return (
    <span
      className={`font-display font-bold tracking-tight ${sizeClasses[size]} text-navy-800 dark:text-gold-500 ${className}`}
    >
      Daily Reprieve
    </span>
  );
}

// Full logo with emblem and optional text
export function Logo({
  variant = 'full',
  size = 'md',
  className = '',
  ...props
}: LogoProps) {
  const containerSizes = {
    sm: 'gap-2',
    md: 'gap-3',
    lg: 'gap-4',
    xl: 'gap-4',
  };

  if (variant === 'emblem') {
    return <LogoEmblem size={size} className={className} {...props} />;
  }

  if (variant === 'wordmark') {
    return <LogoWordmark size={size} className={className} />;
  }

  // Full variant - emblem + wordmark
  return (
    <div className={`flex items-center ${containerSizes[size]} ${className}`}>
      <LogoEmblem size={size} {...props} />
      <LogoWordmark size={size} />
    </div>
  );
}

export default Logo;
